import debounce from "lodash/debounce";
import { jsx } from "panel";
import { ButtonEl } from "common/components/button";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { CalendarEl } from "common/components/calendar";
import { InputEl } from "common/components/input";
import { ProgressBarEl } from "common/components/progress-bar";
import { SelectEl } from "common/components/select";
import { SvgIconEl } from "common/components/svg-icon";
import { TileEl, TileTheme } from "common/components/tile";
import { QueryEntrySectionEl } from "common/query-builder/query-entry-section/panel";
import {
  RowLabelType,
  SelectorTypeDefinitions
} from "common/query-builder/query-entry-section/types";
import { SaaSCompanyKpiTemplateFields, TemplateType } from "common/report/dashboards/types";
import { SpinnerEl } from "common/components/spinner";
import { SpinnerColor } from "common/components/spinner/types";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { MarkdownEl } from "common/components/markdown";
import { dataGroupStore } from "common/report/stores/data-groups";
import { MenuItemType } from "common/query-builder/query-block/properties-menu/types";
import { PropertyType } from "common/report/constants";
import { EventAndListPropertySelector } from "./components/EventAndListPropertySelector";
import { FieldLabel } from "./components/FieldLabel";
import {
  BenchmarkKPITemplateFields,
  ECommerceTemplateFields,
  KPITemplateFields,
  LEARN_MORE_LINK_MAP,
  LifecycleCohortAnalysisTemplateFields,
  MarketingKPITemplateFields,
  ProductLaunchTemplateFields,
  RHSInputType,
  RHSPanelMode,
  TemplateFieldLabelTranslation,
  WebAnalyticsTemplateFields
} from "./types";
import calendarButtonOverride from "./calendar-button-override.inline.styl";
import { EventAndPropertySelectorEl } from "./components/EventAndPropertySelector";
const commonParams = {
  buttonTheme: ButtonTheme.QueryBuilder,
  disableAddEventFilters: true,
  disableCloneEntry: true,
  disableDeleteEntry: false,
  disableFirstTimeEventFilters: true,
  rowLimit: 1,
  rowLabelType: RowLabelType.None
};
export const PAGE_LOADED_TOOLTIP = ({ t }) => t(`boards.rhs.web-analytics.tooltip.page-loaded`);
export const PAGE_NAME_TOOLTIP = ({ t }) => t(`boards.rhs.web-analytics.tooltip.page-name`);
const VALUE_MOMENT_FIELD_TOOLTIP = ({ t }) => t(`boards.rhs.value-moment-tooltip`);
export function template() {
  const preselectedEventsSectionFactory = (eventName, tooltipText = ``, infoText = ``, showReminderTooltip) => {
    return /* @__PURE__ */ jsx("div", { sel: ".event-mapping-row.short-field" }, /* @__PURE__ */ jsx(
      FieldLabel,
      {
        fieldName: this.print(TemplateFieldLabelTranslation[eventName]),
        tooltipText: this.print(tooltipText)
      }
    ), infoText && /* @__PURE__ */ jsx("div", { sel: ".info-text" }, this.print(infoText)), /* @__PURE__ */ jsx(
      QueryEntrySectionEl,
      {
        ...commonParams,
        addEntryButtonLabel: ({ t }) => t(`boards.rhs.add-event`),
        projectId: parseInt(this.getContext(`currentProject`).id, 10),
        trackName: "RHS",
        onUpdate: (updates) => this.helpers.handleEventUpdate(updates, eventName),
        sel: ".query-entry-section"
      }
    ), showReminderTooltip && /* @__PURE__ */ jsx(
      TooltipEl,
      {
        attrs: {
          placement: Placement.Left,
          offset: 28,
          skidding: infoText ? 58 : 40,
          // add more space if infoText shown
          "always-show": true,
          "delay-in": 10,
          "delay-out": 10,
          "max-width": `200px`
        }
      },
      this.t(`boards.rhs.reminder-tooltip`)
    ));
  };
  const PropertiesSelector = ({
    propertyKey,
    tooltipText,
    infoText,
    extraQueryEntrySectionProps = {}
  }) => {
    return /* @__PURE__ */ jsx("div", { sel: ".event-mapping-row.short-field" }, /* @__PURE__ */ jsx(
      FieldLabel,
      {
        fieldName: this.print(TemplateFieldLabelTranslation[propertyKey]),
        tooltipText: this.print(tooltipText)
      }
    ), infoText && /* @__PURE__ */ jsx("div", { sel: ".info-text" }, infoText), /* @__PURE__ */ jsx(
      QueryEntrySectionEl,
      {
        ...commonParams,
        sel: ".query-entry-section",
        disableAddEntryButtonTooltipText: true,
        disableMapToLookupTable: true,
        ...this.state.fields[propertyKey] ? {
          entries: [this.state.fields[propertyKey]]
        } : {},
        disableTypecast: true,
        addEntryButtonLabel: ({ t }) => t(`boards.rhs.add-property`),
        projectId: parseInt(this.getContext(`currentProject`).id, 10),
        rowTypes: [SelectorTypeDefinitions.Segment],
        onUpdate: (updates) => {
          if (updates.entries.length) {
            this.helpers.updateField(updates.updatedEntry, propertyKey, RHSInputType.Property);
          } else {
            this.helpers.removeField(propertyKey);
          }
        },
        ...extraQueryEntrySectionProps
      }
    ));
  };
  const templateCalendarInputField = (fieldName) => {
    return /* @__PURE__ */ jsx("div", { sel: ".event-mapping-row.short-field" }, /* @__PURE__ */ jsx(FieldLabel, { fieldName: this.print(TemplateFieldLabelTranslation[fieldName]) }), /* @__PURE__ */ jsx(
      ButtonEl,
      {
        attrs: {
          icon: `calendar`,
          theme: ButtonTheme.TemplatesEvent,
          pressed: this.state.calendarInputOpen,
          elref: `calendarButton`,
          "style-override": calendarButtonOverride
        },
        on: { click: () => this.update({ calendarInputOpen: true }) }
      },
      this.state.fields[fieldName] || this.t(`boards.rhs.select-date`)
    ), this.state.calendarInputOpen && /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx(
      CalendarEl,
      {
        attrs: {
          "is-double-calendar": false,
          "is-range": false,
          "is-from-date-focused": true
        },
        on: {
          selectDate: (ev) => {
            this.helpers.updateField(ev.detail.date, fieldName, RHSInputType.Date);
            this.update({ calendarInputOpen: false });
          }
        }
      }
    )));
  };
  const debouncedHandleInput = debounce((value, fieldName) => {
    this.helpers.updateField(`\u2018${value}\u2019`, fieldName, RHSInputType.Text);
  }, 500);
  const templateInputField = (fieldName, placeholder) => {
    return /* @__PURE__ */ jsx("div", { sel: ".event-mapping-row" }, /* @__PURE__ */ jsx(FieldLabel, { fieldName: this.print(TemplateFieldLabelTranslation[fieldName]) }), /* @__PURE__ */ jsx(
      InputEl,
      {
        attrs: {
          placeholder,
          "style-override": `.mp-input-container input.mp-input {background-color: var(--background-color)}`
        },
        on: {
          input: (ev) => debouncedHandleInput(ev.target.value, fieldName)
        }
      }
    ));
  };
  const kpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      KPITemplateFields.SIGN_UP,
      this.t(`boards.rhs.kpi.tooltip.sign-up`, { urlLink: this.WHAT_TO_TRACK_URL }),
      null,
      this.helpers.shouldShowReminderTooltip()
    ), preselectedEventsSectionFactory(KPITemplateFields.VALUE_MOMENT, VALUE_MOMENT_FIELD_TOOLTIP));
  };
  const productLaunchTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, templateInputField(
      ProductLaunchTemplateFields.FEATURE_NAME,
      this.t(`boards.rhs.product-launch.placeholder.feature-name`)
    ), templateCalendarInputField(ProductLaunchTemplateFields.LAUNCH_DATE), preselectedEventsSectionFactory(
      ProductLaunchTemplateFields.NEW_FEATURE,
      this.t(`boards.rhs.product-launch.tooltip.new-feature`),
      null,
      this.helpers.shouldShowReminderTooltip()
    ), preselectedEventsSectionFactory(ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT, VALUE_MOMENT_FIELD_TOOLTIP));
  };
  const lifecycleCohortAnalysisTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      LifecycleCohortAnalysisTemplateFields.VALUE_MOMENT,
      VALUE_MOMENT_FIELD_TOOLTIP,
      null,
      this.helpers.shouldShowReminderTooltip()
    ));
  };
  const marketingKpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      MarketingKPITemplateFields.ACTIVATION_EVENT,
      this.t(`boards.rhs.marketing-kpi.tooltip.activation`),
      this.t(`boards.rhs.marketing-kpi.info-text.activation`),
      this.helpers.shouldShowReminderTooltip()
    ), preselectedEventsSectionFactory(
      MarketingKPITemplateFields.CONVERSION_EVENT,
      this.t(`boards.rhs.marketing-kpi.tooltip.conversion`)
    ));
  };
  const eCommerceTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      ECommerceTemplateFields.SIGN_UP_EVENT,
      this.t(`boards.rhs.ecommerce.tooltip.sign-up`, { urlLink: this.WHAT_TO_TRACK_URL })
    ), EventAndListPropertySelector({
      cmp: this,
      eventName: ECommerceTemplateFields.PURCHASE_EVENT,
      propertyName: ECommerceTemplateFields.CART_PROPERTY,
      propertyKey: ECommerceTemplateFields.PRICE_KEY,
      label: this.t(`boards.rhs.ecommerce.label.purchase-cart-price`),
      disabled: false,
      tooltipText: this.t(`boards.rhs.ecommerce.tooltip.purchase-cart-price`, {
        urlLink: this.CART_ANALYSIS_URL
      }),
      event: ECommerceTemplateFields.PURCHASE_EVENT,
      propertyNameLabel: this.t(`boards.rhs.ecommerce.label.cart`),
      propertyKeyLabel: this.t(`boards.rhs.ecommerce.label.price`),
      showReminderTooltip: this.helpers.shouldShowReminderTooltip()
    }), EventAndListPropertySelector({
      cmp: this,
      eventName: ECommerceTemplateFields.ADD_TO_CART_EVENT,
      propertyName: ECommerceTemplateFields.CART_PROPERTY_2,
      propertyKey: ECommerceTemplateFields.CATEGORY_KEY,
      label: this.t(`boards.rhs.ecommerce.label.add-to-cart-cart-category`),
      disabled: false,
      tooltipText: this.t(`boards.rhs.ecommerce.tooltip.add-to-cart-cart-category`, {
        urlLink: this.CART_ANALYSIS_URL
      }),
      event: ECommerceTemplateFields.ADD_TO_CART_EVENT,
      propertyNameLabel: this.t(`boards.rhs.ecommerce.label.cart`),
      propertyKeyLabel: this.t(`boards.rhs.ecommerce.label.category`)
    }), preselectedEventsSectionFactory(
      ECommerceTemplateFields.PRODUCT_VIEWED_EVENT,
      this.t(`boards.rhs.ecommerce.tooltip.product-viewed`, { urlLink: this.WHAT_TO_TRACK_URL })
    ), /* @__PURE__ */ jsx(
      EventAndPropertySelectorEl,
      {
        eventKey: ECommerceTemplateFields.AD_SPEND_EVENT,
        propertyKey: ECommerceTemplateFields.AD_COST_PROPERTY,
        label: this.t(`boards.rhs.ecommerce.label.ad-spend`),
        disabled: false,
        tooltipText: this.t(`boards.rhs.ecommerce.tooltip.ad-spend`),
        event: ECommerceTemplateFields.AD_SPEND_EVENT,
        propertyKeyLabel: this.t(`boards.rhs.ecommerce.label.ad-cost-property`),
        updateField: this.helpers.updateField.bind(this),
        update: this.update.bind(this),
        removeField: this.helpers.removeField.bind(this),
        currentProjectId: parseInt(this.getContext(`currentProject`).id, 10),
        eventField: this.state.fields[ECommerceTemplateFields.AD_SPEND_EVENT],
        propertyField: this.state.fields[ECommerceTemplateFields.AD_COST_PROPERTY]
      }
    ));
  };
  const webAnalyticsTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      WebAnalyticsTemplateFields.PAGE_LOADED_EVENT,
      PAGE_LOADED_TOOLTIP,
      this.t(`boards.rhs.web-analytics.info-text.page-loaded`),
      this.helpers.shouldShowReminderTooltip()
    ), /* @__PURE__ */ jsx(
      PropertiesSelector,
      {
        propertyKey: WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY,
        tooltipText: PAGE_NAME_TOOLTIP,
        infoText: this.t(`boards.rhs.web-analytics.info-text.page-name`)
      }
    ));
  };
  const benchmarkIndustrySelector = () => {
    var _a;
    return /* @__PURE__ */ jsx("div", { sel: ".event-mapping-row.short-field" }, /* @__PURE__ */ jsx(FieldLabel, { fieldName: "Industry" }), /* @__PURE__ */ jsx(
      SelectEl,
      {
        sel: ".benchmark-industry-selector",
        attrs: {
          "menu-height": 500,
          "selected-option": (_a = this.state.fields[BenchmarkKPITemplateFields.INDUSTRY]) != null ? _a : `All`,
          sections: [
            {
              items: [
                `All`,
                `Technology`,
                `Media & Entertainment`,
                `Financial Services`,
                `Retailing`,
                `Healthcare`,
                `Gaming`
              ].map((item) => ({
                label: item,
                value: item
              }))
            }
          ]
        },
        on: {
          change: (ev) => {
            this.helpers.updateField(ev.detail.value, BenchmarkKPITemplateFields.INDUSTRY, RHSInputType.Text);
          }
        }
      }
    ));
  };
  const benchmarkKpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      BenchmarkKPITemplateFields.SIGN_UP,
      this.t(`boards.rhs.kpi.tooltip.sign-up`, { urlLink: this.WHAT_TO_TRACK_URL }),
      // using same company KPI tooltip as benchmark template is deprecated
      null,
      this.helpers.shouldShowReminderTooltip()
    ), preselectedEventsSectionFactory(BenchmarkKPITemplateFields.VALUE_MOMENT, VALUE_MOMENT_FIELD_TOOLTIP), benchmarkIndustrySelector());
  };
  const saasCompanyKpiTemplateFields = () => {
    var _a;
    const selectedDataGroupId = this.state.fields[SaaSCompanyKpiTemplateFields.DATA_GROUP_ID];
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".event-mapping-row.short-field" }, /* @__PURE__ */ jsx(
      FieldLabel,
      {
        fieldName: SaaSCompanyKpiTemplateFields.DATA_GROUP_ID,
        tooltipText: "Pick a group profile that represents a customer account."
      }
    ), /* @__PURE__ */ jsx(
      SelectEl,
      {
        attrs: {
          label: !selectedDataGroupId ? `Select a data group...` : ``,
          "menu-width": 200,
          size: ButtonSize.Large,
          sections: [
            {
              items: Object.values((_a = dataGroupStore.value) != null ? _a : {}).map((dg) => ({
                label: dg.displayName,
                value: dg.groupID,
                isSelected: dg.groupID === selectedDataGroupId
              }))
            }
          ]
        },
        on: {
          change: (ev) => {
            this.helpers.removeField(SaaSCompanyKpiTemplateFields.MRR_PROPERTY_FIELD);
            this.helpers.removeField(SaaSCompanyKpiTemplateFields.PLAN_TYPE_PROPERTY_FIELD);
            this.helpers.removeField(SaaSCompanyKpiTemplateFields.SEGMENT_PROPERTY_FIELD);
            this.helpers.updateField(
              ev.detail.item.value,
              SaaSCompanyKpiTemplateFields.DATA_GROUP_ID,
              RHSInputType.Text
            );
          }
        }
      }
    )), /* @__PURE__ */ jsx(
      PropertiesSelector,
      {
        propertyKey: SaaSCompanyKpiTemplateFields.MRR_PROPERTY_FIELD,
        tooltipText: `Profile property that represents the MRR (Monthly Recurring Revenue) of a customer account.`,
        infoText: "This should be a numerical profile historical property.",
        extraQueryEntrySectionProps: {
          propertiesMenuItemTypes: [MenuItemType.SCDProperty],
          propertiesMenuPropertyTypes: [PropertyType.Number],
          dataGroupId: selectedDataGroupId,
          disabled: !selectedDataGroupId,
          dataGroupsEnabled: true,
          projectHasPeople: true,
          hideEvents: true,
          disableScdOptions: true
        }
      }
    ), /* @__PURE__ */ jsx(
      PropertiesSelector,
      {
        propertyKey: SaaSCompanyKpiTemplateFields.PLAN_TYPE_PROPERTY_FIELD,
        tooltipText: `Plan Type of group e.g. Basic, Pro, Enterprise.`,
        infoText: "This should be a string historical property.",
        extraQueryEntrySectionProps: {
          propertiesMenuItemTypes: [MenuItemType.SCDProperty],
          propertiesMenuPropertyTypes: [PropertyType.String],
          disabled: !selectedDataGroupId,
          dataGroupId: selectedDataGroupId,
          dataGroupsEnabled: true,
          projectHasPeople: true,
          hideEvents: true,
          disableScdOptions: true
        }
      }
    ), /* @__PURE__ */ jsx(
      PropertiesSelector,
      {
        propertyKey: SaaSCompanyKpiTemplateFields.SEGMENT_PROPERTY_FIELD,
        tooltipText: `Segment of group e.g. MM+, Enterprise, SMB`,
        infoText: "This should be a string property.",
        extraQueryEntrySectionProps: {
          propertiesMenuPropertyTypes: [PropertyType.String],
          disabled: !selectedDataGroupId,
          dataGroupId: selectedDataGroupId,
          dataGroupsEnabled: true,
          projectHasPeople: true,
          hideEvents: true,
          disableScdOptions: true
        }
      }
    ));
  };
  const templateFields = () => {
    switch (this.params.selectedTemplate) {
      case TemplateType.KPI:
        return kpiTemplateFields();
      case TemplateType.FEATURE_LAUNCH:
        return productLaunchTemplateFields();
      case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
        return lifecycleCohortAnalysisTemplateFields();
      case TemplateType.MARKETING_KPI:
        return marketingKpiTemplateFields();
      case TemplateType.E_COMMERCE:
        return eCommerceTemplateFields();
      case TemplateType.WEB_ANALYTICS:
        return webAnalyticsTemplateFields();
      case TemplateType.BENCHMARK_KPI:
        return benchmarkKpiTemplateFields();
      case TemplateType.SAAS_COMPANY_KPI:
        return saasCompanyKpiTemplateFields();
      default:
        return null;
    }
  };
  const progressBar = (length) => {
    return /* @__PURE__ */ jsx("div", { sel: ".progress-bar-container" }, /* @__PURE__ */ jsx("div", { sel: ".current-step" }, this.state.tutorialCurrentStepIndex + 1 === length ? this.t(`boards.rhs.tutorial.completed`) : this.t(`boards.rhs.tutorial.progress-count`, {
      currentStep: this.state.tutorialCurrentStepIndex + 1,
      totalStep: length - 1
    })), /* @__PURE__ */ jsx(
      ProgressBarEl,
      {
        sel: ".progress-bar-meter",
        attrs: { percent: (this.state.tutorialCurrentStepIndex + 1) / length * 100 }
      }
    ));
  };
  const eventsInfo = () => {
    const learnMoreLink = LEARN_MORE_LINK_MAP.get(this.params.selectedTemplate);
    let link;
    if (this.params.selectedTemplate === TemplateType.E_COMMERCE) {
      link = this.t(`boards.rhs.setup.learn-more-link`, { urlLink: learnMoreLink });
    } else if (this.params.selectedTemplate === TemplateType.LIFECYCLE_COHORT_ANALYSIS) {
      link = this.t(`boards.rhs.setup.value-moment-link`, { urlLink: this.VALUE_MOMENT_URL });
    } else if (this.params.selectedTemplate === TemplateType.SAAS_COMPANY_KPI) {
      link = null;
    } else {
      link = this.t(`boards.rhs.setup.why-events-link`, { urlLink: this.WHY_THESE_EVENTS_URL });
    }
    return /* @__PURE__ */ jsx("div", { sel: ".events-info" }, this.helpers.getInfoEventString(), ` `, link && /* @__PURE__ */ jsx(
      MarkdownEl,
      {
        sel: ".inline-link",
        attrs: {
          text: link
        }
      }
    ));
  };
  const tutorial = () => {
    const { showTutorial } = this.state;
    const tutorialConfig = this.helpers.getTemplateTutorialConfig();
    if (tutorialConfig && !this.state.hideTutorial) {
      return /* @__PURE__ */ jsx(
        TileEl,
        {
          sel: ".info-tile",
          params: {
            theme: TileTheme.NO_IMAGE,
            fillWidth: true
          }
        },
        /* @__PURE__ */ jsx(null, null, showTutorial ? /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx(
          MarkdownEl,
          {
            attrs: {
              text: this.print(Object.values(tutorialConfig)[this.state.tutorialCurrentStepIndex].stepInfo)
            }
          }
        ), progressBar(Object.values(tutorialConfig).length)) : /* @__PURE__ */ jsx(null, null, this.t(`boards.rhs.tutorial.info-text`), /* @__PURE__ */ jsx("div", { sel: ".tutorial-button-container" }, /* @__PURE__ */ jsx(
          ButtonEl,
          {
            attrs: { size: ButtonSize.Small, theme: ButtonTheme.Primary },
            on: { click: this.helpers.startTutorial }
          },
          this.t(`boards.rhs.tutorial.show-me`)
        ), /* @__PURE__ */ jsx(ButtonEl, { attrs: { size: ButtonSize.Small }, on: { click: () => this.helpers.dismissTutorial() } }, this.t(`boards.rhs.tutorial.not-now`)))))
      );
    }
    return null;
  };
  const eventSelectStep = () => {
    return /* @__PURE__ */ jsx("div", { sel: ".event-select-step" }, eventsInfo(), tutorial(), templateFields());
  };
  return /* @__PURE__ */ jsx("div", { sel: ".right-panel" }, /* @__PURE__ */ jsx(
    "div",
    {
      sel: ".container",
      class: {
        templates: this.params.mode === RHSPanelMode.Templates,
        [`dark-mode`]: this.getContext(`themes`).isDarkMode()
      }
    },
    this.params.mode === RHSPanelMode.Templates && /* @__PURE__ */ jsx("div", { sel: ".templates-back-btn" }, this.params.isSeeAllTemplatesLoading ? /* @__PURE__ */ jsx(SpinnerEl, { attrs: { color: SpinnerColor.Inherit } }) : /* @__PURE__ */ jsx("div", { sel: ".click-area", on: { click: this.helpers.handleSeeAllTemplatesClick } }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: { icon: `arrow-left` } }), /* @__PURE__ */ jsx("span", null, this.t(`boards.rhs.see-all-templates`)))),
    /* @__PURE__ */ jsx("div", { sel: ".header", class: { templates: this.params.mode === RHSPanelMode.Templates } }, this.t(`boards.rhs.setup.header`)),
    /* @__PURE__ */ jsx("div", { sel: ".body", class: { templates: this.params.mode === RHSPanelMode.Templates } }, this.params.mode === RHSPanelMode.StarterBoard && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".event-description" }, /* @__PURE__ */ jsx("div", { sel: ".description-title" }, this.t(`boards.rhs.starter-board-title`)), /* @__PURE__ */ jsx("div", { sel: ".description-body" }, this.t(`boards.rhs.starter-board-description`))), /* @__PURE__ */ jsx("div", { sel: ".event-item" }, /* @__PURE__ */ jsx("div", { sel: ".icon" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: { icon: `custom-event-beta` } })), /* @__PURE__ */ jsx("div", { sel: ".event-name" }, this.t(`boards.rhs.all-events`)))), this.params.mode === RHSPanelMode.Templates && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", null, eventSelectStep()), /* @__PURE__ */ jsx(
      ButtonEl,
      {
        sel: ".submit-btn",
        attrs: {
          theme: ButtonTheme.Primary,
          disabled: this.helpers.shouldDisableFinishButton(),
          elref: `createTemplateButton`,
          icon: this.params.isLoading ? `spinner` : ``
        },
        on: {
          click: this.helpers.createTemplateBoard
        }
      },
      this.t(`common.save`)
    )))
  ));
}
